import React from 'react';
import { CellPosition, SpreadsheetData } from '../types';

interface UseSpreadsheetSearchReturn {
  effectiveSearchTerm: string;
  effectiveCurrentIndex: number;
  effectiveTotalResults: number;
  searchResults: CellPosition[];
  showSearchBar: boolean;
  setShowSearchBar: (show: boolean) => void;
  handleSearchChange: (term: string) => void;
  handleNextSearch: () => void;
  handlePreviousSearch: () => void;
  handleClearSearch: () => void;
  getCurrentSearchResult: () => CellPosition | null;
  isCellSearchMatch: (rowIdx: number, columnKey: string, value: any) => boolean;
  isCellCurrentSearchResult: (rowIdx: number, columnKey: string) => boolean;
  navigateToSearchResult: (
    index: number,
    containerRef: React.RefObject<HTMLDivElement>,
    rowHeight: number,
    columnWidths?: number[],
    showRowNumbers?: boolean
  ) => void;
  navigateToSearchIndex: (
    index: number,
    containerRef: React.RefObject<HTMLDivElement>,
    rowHeight: number,
    columnWidths?: number[],
    showRowNumbers?: boolean
  ) => void;
}

interface UseSpreadsheetSearchProps {
  spreadsheetData: SpreadsheetData[];
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  currentSearchIndex?: number;
  totalSearchResults?: number;
  onNextSearch?: () => void;
  onPreviousSearch?: () => void;
  onClearSearch?: () => void;
}

export function useSpreadsheetSearch({
  spreadsheetData,
  searchTerm,
  onSearchChange,
  currentSearchIndex,
  totalSearchResults,
  onNextSearch,
  onPreviousSearch,
  onClearSearch
}: UseSpreadsheetSearchProps): UseSpreadsheetSearchReturn {
  // Internal search state
  const [internalSearchTerm, setInternalSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<CellPosition[]>([]);
  const [currentSearchResultIndex, setCurrentSearchResultIndex] = React.useState(0);
  const [showSearchBar, setShowSearchBar] = React.useState(false);

  // Use external search state if provided, otherwise use internal state
  const effectiveSearchTerm = searchTerm !== undefined ? searchTerm : internalSearchTerm;
  const effectiveCurrentIndex = currentSearchIndex !== undefined ? currentSearchIndex : currentSearchResultIndex;
  const effectiveTotalResults = totalSearchResults !== undefined ? totalSearchResults : searchResults.length;

  // Search functionality
  const findSearchResults = React.useCallback((term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setCurrentSearchResultIndex(0);
      return;
    }

    const results: CellPosition[] = [];
    const searchLower = term.toLowerCase();

    spreadsheetData.forEach((row, rowIdx) => {
      Object.keys(row).forEach(columnKey => {
        if (columnKey === '__rowNumber') return;
        
        const value = row[columnKey];
        if (value && typeof value === 'string' && value.toLowerCase().includes(searchLower)) {
          results.push({ rowIdx, columnKey });
        }
      });
    });

    setSearchResults(results);
    setCurrentSearchResultIndex(results.length > 0 ? 0 : -1);
  }, [spreadsheetData]);

  // Update search results when search term changes
  React.useEffect(() => {
    findSearchResults(effectiveSearchTerm);
  }, [effectiveSearchTerm, findSearchResults]);

  // Handle search term change
  const handleSearchChange = React.useCallback((term: string) => {
    if (onSearchChange) {
      onSearchChange(term);
    } else {
      setInternalSearchTerm(term);
    }
  }, [onSearchChange]);

  // Handle next search result
  const handleNextSearch = React.useCallback(() => {
    if (onNextSearch) {
      onNextSearch();
    } else {
      const nextIndex = (effectiveCurrentIndex + 1) % searchResults.length;
      setCurrentSearchResultIndex(nextIndex);
    }
  }, [effectiveCurrentIndex, searchResults.length, onNextSearch]);

  // Handle previous search result
  const handlePreviousSearch = React.useCallback(() => {
    if (onPreviousSearch) {
      onPreviousSearch();
    } else {
      const prevIndex = effectiveCurrentIndex <= 0 ? searchResults.length - 1 : effectiveCurrentIndex - 1;
      setCurrentSearchResultIndex(prevIndex);
    }
  }, [effectiveCurrentIndex, searchResults.length, onPreviousSearch]);

  // Handle clear search
  const handleClearSearch = React.useCallback(() => {
    if (onClearSearch) {
      onClearSearch();
    } else {
      setInternalSearchTerm('');
      setSearchResults([]);
      setCurrentSearchResultIndex(0);
    }
  }, [onClearSearch]);

  // Get current search result
  const getCurrentSearchResult = React.useCallback((): CellPosition | null => {
    if (searchResults.length === 0 || effectiveCurrentIndex < 0 || effectiveCurrentIndex >= searchResults.length) {
      return null;
    }
    return searchResults[effectiveCurrentIndex];
  }, [searchResults, effectiveCurrentIndex]);

  // Check if a cell is a search match
  const isCellSearchMatch = React.useCallback((_: number, __: string, value: any): boolean => {
    return Boolean(effectiveSearchTerm) && 
      typeof value === 'string' && 
      value.toLowerCase().includes(effectiveSearchTerm.toLowerCase());
  }, [effectiveSearchTerm]);

  // Check if a cell is the current search result
  const isCellCurrentSearchResult = React.useCallback((rowIdx: number, columnKey: string): boolean => {
    const currentResult = getCurrentSearchResult();
    return currentResult?.rowIdx === rowIdx && currentResult?.columnKey === columnKey;
  }, [getCurrentSearchResult]);

  // Navigate to search result with scrolling
  const navigateToSearchResult = React.useCallback((
    index: number, 
    containerRef: React.RefObject<HTMLDivElement>,
    rowHeight: number,
    columnWidths?: number[],
    showRowNumbers: boolean = true
  ) => {
    if (searchResults.length === 0 || index < 0 || index >= searchResults.length) return;

    const result = searchResults[index];
    
    // Improved scroll to the cell - find the correct scrollable container
    setTimeout(() => {
      if (!containerRef.current) return;

      // Try multiple strategies to find the scrollable container
      let scrollableContainer: HTMLElement | null = null;
      
      // Strategy 1: Look for the main DataGrid container with scrollable properties
      const possibleContainers = [
        containerRef.current.querySelector('[role="grid"]'),
        containerRef.current.querySelector('.rdg'),
        containerRef.current.querySelector('div[style*="overflow"]'),
        containerRef.current.querySelector('div[style*="auto"]'),
        containerRef.current.querySelector('div[style*="scroll"]'),
      ];
      
      for (const container of possibleContainers) {
        if (container && container instanceof HTMLElement) {
          const computedStyle = window.getComputedStyle(container);
          if (computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll' || 
              computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll' ||
              computedStyle.overflowX === 'auto' || computedStyle.overflowX === 'scroll') {
            scrollableContainer = container;
            break;
          }
        }
      }
      
      // Strategy 2: If no scrollable container found, use the first div child
      if (!scrollableContainer) {
        const firstDiv = containerRef.current.querySelector('div');
        if (firstDiv instanceof HTMLElement) {
          scrollableContainer = firstDiv;
        }
      }
      
      // Strategy 3: Use the containerRef itself as last resort
      if (!scrollableContainer) {
        scrollableContainer = containerRef.current;
      }
      
      if (!scrollableContainer) return;

      // Calculate the target scroll position
      const rowNumberWidth = showRowNumbers ? 50 : 0;
      
      // Calculate row position
      const targetRowTop = result.rowIdx * rowHeight;
      
      // Calculate column position
      const getColumnIndex = (columnKey: string) => {
        if (columnKey === '__rowNumber') return -1;
        return columnKey.charCodeAt(0) - 65;
      };
      
      const colIndex = getColumnIndex(result.columnKey);
      let targetColLeft = rowNumberWidth;
      for (let i = 0; i < colIndex; i++) {
        targetColLeft += columnWidths?.[i] || 100;
      }
      
      // Get container dimensions
      const containerHeight = scrollableContainer.clientHeight;
      const containerWidth = scrollableContainer.clientWidth;
      
      // Calculate target scroll positions to center the cell
      const targetScrollTop = Math.max(0, targetRowTop - (containerHeight / 2) + (rowHeight / 2));
      const targetScrollLeft = Math.max(0, targetColLeft - (containerWidth / 2) + ((columnWidths?.[colIndex] || 100) / 2));
      
      // Try smooth scrolling first
      try {
        scrollableContainer.scrollTo({
          top: targetScrollTop,
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      } catch (error) {
        // Fallback to immediate scrolling if smooth scrolling fails
        console.warn('Smooth scrolling failed, using fallback:', error);
        scrollableContainer.scrollTop = targetScrollTop;
        scrollableContainer.scrollLeft = targetScrollLeft;
      }
      
      // Also try to find and highlight the cell element for visual feedback
      const cellSelectors = [
        `input[data-row="${result.rowIdx}"][data-col="${result.columnKey}"]`,
        `[data-row="${result.rowIdx}"][data-col="${result.columnKey}"]`,
        `[data-row="${result.rowIdx}"] [data-col="${result.columnKey}"]`,
      ];
      
      let cellElement: Element | null = null;
      for (const selector of cellSelectors) {
        cellElement = containerRef.current.querySelector(selector);
        if (cellElement) break;
      }
      
      if (cellElement) {
        // Add a temporary highlight effect
        cellElement.classList.add('search-highlight');
        setTimeout(() => {
          cellElement?.classList.remove('search-highlight');
        }, 2000);
      }
         }, 100);
   }, [searchResults]);

  // Navigate directly to a specific search result index
  const navigateToSearchIndex = React.useCallback((
    index: number,
    containerRef: React.RefObject<HTMLDivElement>,
    rowHeight: number,
    columnWidths?: number[],
    showRowNumbers: boolean = true
  ) => {
    if (searchResults.length === 0 || index < 0 || index >= searchResults.length) return;
    
    // Update the current search index if using internal state
    if (currentSearchIndex === undefined) {
      setCurrentSearchResultIndex(index);
    }
    
    // Navigate to the search result
    navigateToSearchResult(index, containerRef, rowHeight, columnWidths, showRowNumbers);
  }, [searchResults, currentSearchIndex, navigateToSearchResult]);

  return {
    effectiveSearchTerm,
    effectiveCurrentIndex,
    effectiveTotalResults,
    searchResults,
    showSearchBar,
    setShowSearchBar,
    handleSearchChange,
    handleNextSearch,
    handlePreviousSearch,
    handleClearSearch,
    getCurrentSearchResult,
    isCellSearchMatch,
    isCellCurrentSearchResult,
    navigateToSearchResult,
    navigateToSearchIndex
  };
} 